import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import BrandLogo from "@/components/BrandLogo";

const UPDATED_AT = "2 de setembro de 2026";

function LegalLayout({ badge, title, children, testid }) {
  return (
    <div className="min-h-screen bg-brand-bg text-white relative overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <AppHeader />

      <main className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="text-center mb-12">
          <div className="pill-badge mb-6 mx-auto inline-flex"><span className="dot" /> {badge}</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-4">{title}</h1>
          <p className="text-[13px] text-brand-ink3 font-ui">Última atualização: {UPDATED_AT}</p>
        </div>

        <div className="space-y-10 text-[15px] leading-[1.85] text-brand-ink2">
          {children}
        </div>
      </main>

      <footer className="relative border-t border-brand-line py-10 z-10">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-3">
          <BrandLogo size="md" />
          <div className="flex gap-5 text-[13px] font-ui">
            <Link to="/termos" className="text-brand-ink2 hover:text-white transition-colors">Termos de Uso</Link>
            <Link to="/privacidade" className="text-brand-ink2 hover:text-white transition-colors">Política de Privacidade</Link>
            <Link to="/sobre" className="text-brand-ink2 hover:text-white transition-colors">Sobre</Link>
          </div>
          <div className="text-[12px] text-brand-ink3 font-ui">
            © {new Date().getFullYear()} ValiosIA — Instituto Valiosas de Treinamentos
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl text-white mb-3 tracking-tight">
        <span className="text-brand-rose mr-2">{n}.</span>{title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function Termos() {
  return (
    <LegalLayout badge="Termos" title="Termos de Uso" testid="termos-page">
      <Section n={1} title="Sobre a plataforma">
        <p>
          A <span className="text-white">ValiosIA</span> (valiosia.com.br) é uma plataforma de
          criação de conteúdo estratégico com inteligência artificial voltada a clínicas de
          depilação a laser, operada pelo Instituto Valiosas de Treinamentos (Instituto Gabriela
          Fachini Ltda, CNPJ 21.623.280/0001-64, Joaçaba/SC). Ao criar uma conta ou usar a
          plataforma, você concorda com estes Termos e com a nossa{" "}
          <Link to="/privacidade" className="text-brand-rose hover:text-brand-roseHover">Política de Privacidade</Link>.
        </p>
      </Section>

      <Section n={2} title="Cadastro e conta">
        <p>
          Para usar a ValiosIA você precisa criar uma conta com informações verdadeiras e
          mantê-las atualizadas. Você é responsável por guardar sua senha e por toda atividade
          realizada na sua conta. O serviço destina-se a maiores de 18 anos e a uso profissional.
        </p>
        <p>
          Novas contas podem passar por análise antes da liberação de acesso. Podemos recusar,
          suspender ou encerrar contas que violem estes Termos.
        </p>
      </Section>

      <Section n={3} title="Conteúdo gerado por inteligência artificial">
        <p>
          A plataforma gera roteiros, textos e sugestões por meio de modelos de IA a partir das
          informações que você fornece. Conteúdo gerado por IA pode conter imprecisões:{" "}
          <span className="text-white">revise sempre antes de publicar</span>. As diretrizes de
          conformidade (CFM/Anvisa) embutidas nos agentes são um apoio à criação responsável e
          não substituem a sua responsabilidade profissional nem aconselhamento jurídico ou
          regulatório.
        </p>
        <p>
          Você é a única responsável pelo conteúdo que decidir publicar nos seus canais, incluindo
          sua adequação à legislação e às normas do seu conselho profissional.
        </p>
      </Section>

      <Section n={4} title="Uso permitido">
        <p>É proibido usar a plataforma para:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>criar conteúdo ilegal, enganoso ou que prometa resultados de saúde garantidos;</li>
          <li>violar direitos de terceiros ou enviar dados pessoais de clientes reais sem base legal;</li>
          <li>tentar burlar limites do plano, engenharia reversa ou acesso não autorizado;</li>
          <li>revender o acesso sem autorização por escrito.</li>
        </ul>
      </Section>

      <Section n={5} title="Planos e pagamentos">
        <p>
          A ValiosIA oferece planos com diferentes limites e recursos. Os valores, condições e
          formas de pagamento vigentes são apresentados na página de planos. Podemos ajustar
          preços e recursos com aviso prévio razoável — mudanças não se aplicam retroativamente a
          períodos já pagos.
        </p>
      </Section>

      <Section n={6} title="Propriedade intelectual">
        <p>
          A plataforma, sua marca, metodologia e software pertencem ao Instituto Valiosas de
          Treinamentos. O conteúdo gerado a partir das suas informações (roteiros, copies,
          calendários) é seu para usar livremente nos seus canais. Você nos concede licença para
          processar os dados fornecidos exclusivamente para prestar o serviço.
        </p>
      </Section>

      <Section n={7} title="Colaboradores">
        <p>
          Você pode convidar colaboradores para visualizar seu workspace (limite conforme o
          plano). Colaboradores têm acesso somente leitura ao seu conteúdo e você é responsável
          por quem convida.
        </p>
      </Section>

      <Section n={8} title="Disponibilidade e limitação de responsabilidade">
        <p>
          Trabalhamos para manter a plataforma disponível e segura, mas ela é fornecida “como
          está”, sem garantia de operação ininterrupta. Na máxima extensão permitida em lei, nossa
          responsabilidade total limita-se ao valor pago por você nos 12 meses anteriores ao
          evento. Não respondemos por resultados comerciais do conteúdo publicado.
        </p>
      </Section>

      <Section n={9} title="Encerramento">
        <p>
          Você pode encerrar sua conta a qualquer momento pelo canal de contato. Podemos suspender
          ou encerrar o acesso em caso de violação destes Termos, mediante aviso quando possível.
          Após o encerramento, seus dados seguem o descrito na Política de Privacidade.
        </p>
      </Section>

      <Section n={10} title="Alterações destes Termos">
        <p>
          Podemos atualizar estes Termos para refletir mudanças no serviço ou na legislação.
          Alterações relevantes serão comunicadas na plataforma ou por e-mail. O uso continuado
          após a vigência da nova versão significa concordância.
        </p>
      </Section>

      <Section n={11} title="Contato e foro">
        <p>
          Dúvidas sobre estes Termos:{" "}
          <a href="mailto:contato@valiosia.com.br" className="text-brand-rose hover:text-brand-roseHover">
            contato@valiosia.com.br
          </a>
          . Fica eleito o foro da comarca de Joaçaba/SC, com renúncia a qualquer outro, por mais
          privilegiado que seja, ressalvadas as competências legais do consumidor.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function Privacidade() {
  return (
    <LegalLayout badge="Privacidade" title="Política de Privacidade" testid="privacidade-page">
      <Section n={1} title="Quem somos">
        <p>
          Esta política explica como a <span className="text-white">ValiosIA</span> trata dados
          pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018 — LGPD).
          A controladora dos dados é o Instituto Valiosas de Treinamentos (Instituto Gabriela
          Fachini Ltda, CNPJ 21.623.280/0001-64, Joaçaba/SC). Canal de privacidade:{" "}
          <a href="mailto:contato@valiosia.com.br" className="text-brand-rose hover:text-brand-roseHover">
            contato@valiosia.com.br
          </a>.
        </p>
      </Section>

      <Section n={2} title="Dados que coletamos">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><span className="text-white">Conta:</span> nome, e-mail e senha (armazenada apenas como hash criptográfico), plano e status de acesso.</li>
          <li><span className="text-white">Conteúdo do trabalho:</span> informações da sua marca (brand persona), perfis de cliente ideal (avatares — perfis estratégicos, não cadastros de clientes reais), conversas com os agentes (texto e transcrições de voz), conteúdos gerados, calendário editorial e documentos ou imagens que você anexar.</li>
          <li><span className="text-white">Dados técnicos:</span> registros de acesso (IP, data e hora) exigidos pelo Marco Civil da Internet e um cookie essencial de sessão.</li>
        </ul>
      </Section>

      <Section n={3} title="Conversas por voz">
        <p>
          Na conversa por voz, o áudio é transmitido em tempo real para o provedor de IA (Google
          Gemini) para transcrição e resposta.{" "}
          <span className="text-white">Não armazenamos as gravações de áudio</span> — apenas a
          transcrição em texto fica salva na conversa, como acontece no chat.
        </p>
      </Section>

      <Section n={4} title="Para que usamos os dados">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>prestar o serviço: gerar conteúdo personalizado a partir da sua marca e avatares;</li>
          <li>enviar e-mails transacionais (acesso, redefinição de senha, convites) e o resumo semanal do seu calendário;</li>
          <li>enviar avisos no celular (calendário do dia às 8h e pendências na segunda), só se você ligar essa opção; dá para desligar em Minha conta → App no celular;</li>
          <li>manter a segurança da conta e prevenir fraudes e abusos;</li>
          <li>cumprir obrigações legais.</li>
        </ul>
        <p>
          Não vendemos seus dados e não os usamos para publicidade de terceiros.
        </p>
      </Section>

      <Section n={5} title="Com quem compartilhamos">
        <p>Usamos operadores essenciais à prestação do serviço:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><span className="text-white">Provedores de IA</span> (OpenRouter e Google): processam o conteúdo das conversas para gerar as respostas;</li>
          <li><span className="text-white">Resend:</span> envio de e-mails transacionais;</li>
          <li><span className="text-white">Railway:</span> hospedagem da plataforma e do banco de dados.</li>
        </ul>
        <p>
          Esses provedores podem processar dados fora do Brasil; a transferência internacional
          segue as salvaguardas da LGPD. Também compartilhamos seu conteúdo com colaboradores que
          <span className="text-white"> você mesma convidar</span> (acesso somente leitura) e com
          autoridades quando houver obrigação legal.
        </p>
      </Section>

      <Section n={6} title="Cookies">
        <p>
          Usamos apenas um cookie essencial (httpOnly) para manter sua sessão. Não usamos cookies
          de publicidade ou rastreamento de terceiros.
        </p>
      </Section>

      <Section n={7} title="Retenção e exclusão">
        <p>
          Mantemos seus dados enquanto a conta estiver ativa. Ao solicitar a exclusão da conta,
          removemos ou anonimizamos seus dados pessoais, exceto o que precisarmos manter por
          obrigação legal (por exemplo, registros de acesso pelo prazo do Marco Civil).
        </p>
      </Section>

      <Section n={8} title="Seus direitos (LGPD)">
        <p>
          Você pode solicitar a qualquer momento: confirmação de tratamento, acesso, correção,
          portabilidade, anonimização ou exclusão dos seus dados, informação sobre
          compartilhamentos e revogação de consentimento. É só escrever para{" "}
          <a href="mailto:contato@valiosia.com.br" className="text-brand-rose hover:text-brand-roseHover">
            contato@valiosia.com.br
          </a>{" "}
          — respondemos nos prazos da LGPD. Você também pode reclamar à ANPD.
        </p>
      </Section>

      <Section n={9} title="Segurança">
        <p>
          Adotamos medidas técnicas e organizacionais proporcionais ao risco: criptografia em
          trânsito (HTTPS), senhas com hash, tokens de sessão de curta duração com revogação,
          controle de acesso por papéis e isolamento por workspace.
        </p>
      </Section>

      <Section n={10} title="Alterações desta política">
        <p>
          Podemos atualizar esta política para refletir mudanças no serviço ou na legislação.
          Alterações relevantes serão comunicadas na plataforma ou por e-mail, com a data de
          atualização sempre indicada no topo.
        </p>
      </Section>
    </LegalLayout>
  );
}
